import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import {
  DerinOrganization,
  createOrganization,
  updateOrganization,
  deleteOrganization,
} from "../../api/derin";
import OrganizationDetailView from "./OrganizationDetailView";
import { useToast } from "./Toast";
import { errorMessageOf } from "./errorMessage";

interface OrganizationsSectionProps {
  organizations: DerinOrganization[];
  isLoading: boolean;
  onRefresh: () => void;
}

const OrganizationsSection: React.FC<OrganizationsSectionProps> = ({
  organizations,
  isLoading,
  onRefresh,
}) => {
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Detail view state
  const [selectedOrg, setSelectedOrg] = useState<DerinOrganization | null>(null);

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<DerinOrganization | null>(null);
  const [formData, setFormData] = useState({ name: "", isActive: true });

  // Delete confirmation
  const [openConfirm, setOpenConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DerinOrganization | null>(null);

  const handleOpenCreate = () => {
    setEditingOrg(null);
    setFormData({ name: "", isActive: true });
    setOpenModal(true);
  };

  const handleOpenEdit = (org: DerinOrganization) => {
    setEditingOrg(org);
    setFormData({ name: org.name, isActive: org.isActive });
    setOpenModal(true);
  };

  const handleViewDetails = (org: DerinOrganization) => {
    setSelectedOrg(org);
  };

  const handleBackToList = () => {
    setSelectedOrg(null);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingOrg(null);
    setFormData({ name: "", isActive: true });
  };

  const handleStatusChange = (newStatus: string) => {
    setFormData((prev) => ({ ...prev, isActive: newStatus === "active" }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showError("Organization name is required");
      return;
    }

    // If status changed to inactive, show confirmation dialog
    if (editingOrg && editingOrg.isActive && !formData.isActive) {
      setDeleteTarget(editingOrg);
      setOpenConfirm(true);
      return;
    }

    try {
      setIsSaving(true);
      if (editingOrg) {
        // The /organization/:id endpoint resolves by the primary key `id`, not the
        // `orgId` slug (86bcf6uhc) — sending the slug 404s with "Error saving organization".
        const updated = await updateOrganization(editingOrg.id, {
          name: formData.name,
        });
        // `selectedOrg` is a snapshot taken on drill-in; `onRefresh` refetches the list
        // but not this copy, so the detail header would show the old name until a page
        // reload. Sync it here when the edited org is the one being viewed.
        setSelectedOrg((prev) =>
          prev && prev.id === editingOrg.id ? { ...prev, ...updated } : prev
        );
        showSuccess("Organization updated successfully");
      } else {
        await createOrganization({ name: formData.name });
        showSuccess("Organization created successfully");
      }
      handleCloseModal();
      onRefresh();
    } catch (err) {
      console.error("Error saving organization:", err);
      // A 409 means the name is taken; say so rather than a generic failure.
      showError(
        (err as any)?.response?.status === 409 ? errorMessageOf(err) : "Error saving organization"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (org: DerinOrganization) => {
    setDeleteTarget(org);
    setOpenConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setIsSaving(true);
      // Resolve by the primary key `id`, not the `orgId` slug (86bcf6uhc).
      await deleteOrganization(deleteTarget.id);
      setOpenConfirm(false);
      setDeleteTarget(null);
      handleCloseModal();
      setSelectedOrg(null);
      showSuccess("Organization deleted successfully");
      onRefresh();
    } catch (err: any) {
      console.error("Error deleting organization:", err);
      const message = err?.response?.data?.message || "Error deleting organization. It may have users.";
      showError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // If an organization is selected, show detail view
  if (selectedOrg) {
    return (
      <>
        <OrganizationDetailView
          organization={selectedOrg}
          onBack={handleBackToList}
          onEdit={() => handleOpenEdit(selectedOrg)}
        />

        {/* Edit Modal */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          PaperProps={{
            sx: {
              boxSizing: "border-box",
              width: "512px",
              height: "328px",
              background: "#FFFFFF",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)",
              borderRadius: "10px",
              position: "relative",
              overflow: "visible",
            },
          }}
        >
          {/* Close Button */}
          <Box
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              width: "16px",
              height: "16px",
              right: "17px",
              top: "17px",
              cursor: "pointer",
              opacity: 0.7,
              "&:hover": { opacity: 1 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" />
            </svg>
          </Box>

          {/* DialogHeader */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "0px",
              gap: "8px",
              position: "absolute",
              width: "462px",
              left: "25px",
              top: "25px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "18px",
                letterSpacing: "-0.439453px",
                color: "#0A0A0A",
              }}
            >
              Edit Organization
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#717182",
              }}
            >
              Update the organization details below.
            </Typography>
          </Box>

          {/* Form Content */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "16px 0px 0px",
              gap: "16px",
              position: "absolute",
              width: "462px",
              left: "25px",
              top: "87px",
            }}
          >
            {/* Organization Name Field */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "8px",
                width: "462px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "14px",
                  letterSpacing: "-0.150391px",
                  color: "#0A0A0A",
                }}
              >
                Organization Name
              </Typography>
              <Box
                component="input"
                type="text"
                placeholder="Enter organization name"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                autoFocus
                sx={{
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: "4px 12px",
                  width: "462px",
                  height: "36px",
                  background: "#F3F3F5",
                  boxShadow: "0px 0px 0px 0.332904px rgba(161, 161, 161, 0.055484)",
                  borderRadius: "8px",
                  border: "none",
                  outline: "none",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "17px",
                  letterSpacing: "-0.150391px",
                  color: "#0A0A0A",
                  "&::placeholder": {
                    color: "#717182",
                  },
                }}
              />
            </Box>

            {/* Status Field */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "8px",
                width: "462px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "14px",
                  letterSpacing: "-0.150391px",
                  color: "#0A0A0A",
                }}
              >
                Status
              </Typography>
              <Box
                component="select"
                value={formData.isActive ? "active" : "inactive"}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleStatusChange(e.target.value)}
                sx={{
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0px 12px",
                  width: "462px",
                  height: "36px",
                  background: "#F3F3F5",
                  borderRadius: "8px",
                  border: "none",
                  outline: "none",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "-0.150391px",
                  color: "#0A0A0A",
                  cursor: "pointer",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6L8 10L12 6' stroke='%23717182' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Box>
            </Box>
          </Box>

          {/* DialogFooter */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              gap: "8px",
              position: "absolute",
              width: "462px",
              left: "25px",
              top: "267px",
            }}
          >
            <Button
              onClick={handleCloseModal}
              sx={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "8px 16px",
                height: "36px",
                background: "#FFFFFF",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#0A0A0A",
                textTransform: "none",
                "&:hover": {
                  background: "#F3F3F5",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !formData.name.trim()}
              sx={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "8px 16px",
                height: "36px",
                background: "#030213",
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#FFFFFF",
                textTransform: "none",
                opacity: (!formData.name.trim() || isSaving) ? 0.5 : 1,
                "&:hover": {
                  background: "#1a1a2e",
                },
                "&:disabled": {
                  color: "#FFFFFF",
                  background: "#030213",
                },
              }}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog
          open={openConfirm}
          onClose={() => {
            setOpenConfirm(false);
            setDeleteTarget(null);
          }}
          PaperProps={{
            sx: {
              boxSizing: "border-box",
              width: "512px",
              background: "#FFFFFF",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)",
              borderRadius: "10px",
              padding: "25px",
            },
          }}
        >
          {/* Close Button */}
          <Box
            onClick={() => {
              setOpenConfirm(false);
              setDeleteTarget(null);
            }}
            sx={{
              position: "absolute",
              width: "16px",
              height: "16px",
              right: "17px",
              top: "17px",
              cursor: "pointer",
              opacity: 0.7,
              "&:hover": { opacity: 1 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" />
            </svg>
          </Box>

          {/* Header */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: 3 }}>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "18px",
                letterSpacing: "-0.439453px",
                color: "#0A0A0A",
              }}
            >
              Inactivate Organization?
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#717182",
              }}
            >
              Are you sure you want to inactivate "{deleteTarget?.name}"? This action will delete the organization and cannot be undone.
            </Typography>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              gap: "8px",
            }}
          >
            <Button
              onClick={() => {
                setOpenConfirm(false);
                setDeleteTarget(null);
              }}
              sx={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "8px 16px",
                height: "36px",
                background: "#FFFFFF",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#0A0A0A",
                textTransform: "none",
                "&:hover": {
                  background: "#F3F3F5",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isSaving}
              sx={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "8px 16px",
                height: "36px",
                background: "#DC2626",
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#FFFFFF",
                textTransform: "none",
                "&:hover": {
                  background: "#B91C1C",
                },
                "&:disabled": {
                  background: "#DC2626",
                  opacity: 0.5,
                },
              }}
            >
              {isSaving ? "Deleting..." : "Inactivate"}
            </Button>
          </Box>
        </Dialog>
      </>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        background: "#FFFFFF",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: "14px",
        mt: 4,
        maxWidth: "896px",
        mx: "auto",
      }}
    >
      {/* CardHeader */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0px 24px",
          height: "72px",
        }}
      >
        {/* CardTitle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <EmojiTransportationIcon sx={{ fontSize: 24, color: "#0A0A0A" }} />
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "16px",
              letterSpacing: "-0.3125px",
              color: "#0A0A0A",
            }}
          >
            Accounts
          </Typography>
        </Box>

        {/* New Organization Button */}
        <Button
          startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
          onClick={handleOpenCreate}
          sx={{
            width: "165px",
            height: "32px",
            background: "#002824",
            borderRadius: "8px",
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            textAlign: "center",
            letterSpacing: "-0.150391px",
            color: "#FFFFFF",
            textTransform: "none",
            "&:hover": {
              background: "#003d38",
            },
          }}
        >
          New Organization
        </Button>
      </Box>

      {/* ClientList */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "8px 24px 24px",
          gap: "8px",
        }}
      >
        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 5, width: "100%" }}>
            <CircularProgress />
          </Box>
        ) : organizations.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5, width: "100%" }}>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "14px",
                color: "#717182",
              }}
            >
              No organizations found
            </Typography>
          </Box>
        ) : (
          organizations.map((org) => (
            <Box
              key={org.id}
              onClick={() => handleViewDetails(org)}
              sx={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: "17px 17px 16px",
                width: "100%",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "10px",
                cursor: "pointer",
                "&:hover": {
                  border: "1px solid #000000",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                {/* Organization Info */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      fontSize: "18px",
                      lineHeight: "27px",
                      letterSpacing: "-0.439453px",
                      color: "#0A0A0A",
                    }}
                  >
                    {org.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      letterSpacing: "-0.150391px",
                      color: "#717182",
                    }}
                  >
                    Created
                    <br />
                    {new Date(org.dateCreated).toLocaleDateString()}
                  </Typography>
                </Box>

                {/* Badge */}
                <Box
                  sx={{
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "2px 8px",
                    gap: "4px",
                    background: org.isActive ? "#121212" : "#ECEEF2",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      fontSize: "12px",
                      lineHeight: "16px",
                      color: org.isActive ? "#FFFFFF" : "#030213",
                    }}
                  >
                    {org.isActive ? "Active" : "Inactive"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* Create/Edit Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            width: "512px",
            height: editingOrg ? "auto" : "328px",
            background: "#FFFFFF",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)",
            borderRadius: "10px",
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        {/* Close Button */}
        <Box
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            width: "16px",
            height: "16px",
            right: "17px",
            top: "17px",
            cursor: "pointer",
            opacity: 0.7,
            "&:hover": { opacity: 1 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 4L12 12M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" />
          </svg>
        </Box>

        {/* DialogHeader */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "0px",
            gap: "8px",
            position: "absolute",
            width: "462px",
            left: "25px",
            top: "25px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "18px",
              letterSpacing: "-0.439453px",
              color: "#0A0A0A",
            }}
          >
            {editingOrg ? "Edit Organization" : "Create New Organization"}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#717182",
            }}
          >
            {editingOrg
              ? "Update the organization details below."
              : "Create a new organization to manage users and surveys."}
          </Typography>
        </Box>

        {/* Form Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "16px 0px 0px",
            gap: "16px",
            position: "absolute",
            width: "462px",
            left: "25px",
            top: "87px",
          }}
        >
          {/* Organization Name Field */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "8px",
              width: "462px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "14px",
                letterSpacing: "-0.150391px",
                color: "#0A0A0A",
              }}
            >
              Organization Name
            </Typography>
            <Box
              component="input"
              type="text"
              placeholder="Enter organization name"
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              autoFocus
              sx={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "4px 12px",
                width: "462px",
                height: "36px",
                background: "#F3F3F5",
                boxShadow: "0px 0px 0px 0.332904px rgba(161, 161, 161, 0.055484)",
                borderRadius: "8px",
                border: "none",
                outline: "none",
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "17px",
                letterSpacing: "-0.150391px",
                color: "#0A0A0A",
                "&::placeholder": {
                  color: "#717182",
                },
              }}
            />
          </Box>

          {/* Status Field */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "8px",
              width: "462px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "14px",
                letterSpacing: "-0.150391px",
                color: "#0A0A0A",
              }}
            >
              Status
            </Typography>
            <Box
              component="select"
              value={formData.isActive ? "active" : "inactive"}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleStatusChange(e.target.value)}
              disabled={!editingOrg}
              sx={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0px 12px",
                width: "462px",
                height: "36px",
                background: "#F3F3F5",
                borderRadius: "8px",
                border: "none",
                outline: "none",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#0A0A0A",
                cursor: editingOrg ? "pointer" : "default",
                opacity: editingOrg ? 1 : 0.6,
                appearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6L8 10L12 6' stroke='%23717182' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
              }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Box>
          </Box>
        </Box>

        {/* DialogFooter */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            gap: "8px",
            position: "absolute",
            width: "462px",
            left: "25px",
            top: "267px",
          }}
        >
          {editingOrg && (
            <Button
              onClick={() => {
                handleCloseModal();
                handleDeleteClick(editingOrg);
              }}
              sx={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "8px 16px",
                height: "36px",
                background: "#FFFFFF",
                border: "1px solid #DC2626",
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#DC2626",
                textTransform: "none",
                "&:hover": {
                  background: "#FEF2F2",
                },
              }}
            >
              Delete
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleCloseModal}
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              height: "36px",
              background: "#FFFFFF",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#0A0A0A",
              textTransform: "none",
              "&:hover": {
                background: "#F3F3F5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !formData.name.trim()}
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              height: "36px",
              background: "#030213",
              borderRadius: "8px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#FFFFFF",
              textTransform: "none",
              opacity: (!formData.name.trim() || isSaving) ? 0.5 : 1,
              "&:hover": {
                background: "#1a1a2e",
              },
              "&:disabled": {
                color: "#FFFFFF",
                background: "#030213",
              },
            }}
          >
            {isSaving ? "Saving..." : editingOrg ? "Save Changes" : "Create"}
          </Button>
        </Box>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={openConfirm}
        onClose={() => {
          setOpenConfirm(false);
          setDeleteTarget(null);
        }}
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            width: "512px",
            background: "#FFFFFF",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)",
            borderRadius: "10px",
            padding: "25px",
          },
        }}
      >
        {/* Close Button */}
        <Box
          onClick={() => {
            setOpenConfirm(false);
            setDeleteTarget(null);
          }}
          sx={{
            position: "absolute",
            width: "16px",
            height: "16px",
            right: "17px",
            top: "17px",
            cursor: "pointer",
            opacity: 0.7,
            "&:hover": { opacity: 1 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 4L12 12M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" />
          </svg>
        </Box>

        {/* Header */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: 3 }}>
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "18px",
              letterSpacing: "-0.439453px",
              color: "#0A0A0A",
            }}
          >
            Inactivate Organization?
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#717182",
            }}
          >
            Are you sure you want to inactivate "{deleteTarget?.name}"? This action will delete the organization and cannot be undone.
          </Typography>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button
            onClick={() => {
              setOpenConfirm(false);
              setDeleteTarget(null);
            }}
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              height: "36px",
              background: "#FFFFFF",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#0A0A0A",
              textTransform: "none",
              "&:hover": {
                background: "#F3F3F5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isSaving}
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              height: "36px",
              background: "#DC2626",
              borderRadius: "8px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#FFFFFF",
              textTransform: "none",
              "&:hover": {
                background: "#B91C1C",
              },
              "&:disabled": {
                background: "#DC2626",
                opacity: 0.5,
              },
            }}
          >
            {isSaving ? "Deleting..." : "Inactivate"}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default OrganizationsSection;
